import { useRef, useState } from "react";
import { createContainer } from "unstated-next";
import IResult from "../models/result/IResult";
import * as voicifyApi from '../api';
import {
    PmsOfficeModel,
    PmsProcedureTypeModel,
    PmsProviderModel,
    PmsSlotModel,
    PmsPatientModel,
    PmsCodeModel,
    Cloud9ScheduleViewModel,
    PmsCacheCategoryModel,
    PmsCacheClearModel,
    PmsSlotsRequest,
    PmsPatientSearchRequest
} from "../models/organizations/PmsLookupModels";

export interface PmsLookupState<T> {
    data: T | null;
    isLoading: boolean;
    errors: string[];
}

const emptyLookup = <T>(): PmsLookupState<T> => ({ data: null, isLoading: false, errors: [] });

// Holds the state for one lookup and runs its API call, keeping errors in state instead of throwing.
// Every lookup is a billed PMS call, so successful results are reused for the same request (key) until
// a forced refresh, and a request that is already in flight is shared rather than sent again.
function useLookup<T>(cacheResults: boolean = true) {
    const [state, setState] = useState<PmsLookupState<T>>(emptyLookup<T>());
    const cache = useRef(new Map<string, T>());
    const inFlight = useRef(new Map<string, Promise<IResult<T>>>());
    // The request whose result should be shown; older responses (e.g. for a previous office) are dropped
    const latestKey = useRef<string | null>(null);

    const run = async (key: string, call: () => Promise<IResult<T>>, force: boolean = false): Promise<IResult<T>> => {
        latestKey.current = key;
        if (cacheResults && !force && cache.current.has(key)) {
            const data = cache.current.get(key);
            setState({ data, isLoading: false, errors: [] });
            return { resultType: "Ok", data, errors: [] } as IResult<T>;
        }

        setState(prev => ({ ...prev, isLoading: true, errors: [] }));
        let request = inFlight.current.get(key);
        if (!request) {
            request = call().catch(e => ({
                resultType: "InternalError",
                errors: [e?.toString() || 'Unknown error'],
                data: null
            } as IResult<T>));
            inFlight.current.set(key, request);
        }
        const result = await request;
        inFlight.current.delete(key);

        if (result.resultType === "Ok" && cacheResults) {
            cache.current.set(key, result.data);
        }
        if (latestKey.current !== key) {
            return result;
        }
        if (result.resultType === "Ok") {
            setState({ data: result.data, isLoading: false, errors: [] });
        } else {
            setState({ data: null, isLoading: false, errors: result.errors?.length ? result.errors : ['Lookup failed'] });
        }
        return result;
    };

    const reset = () => {
        latestKey.current = null;
        setState(emptyLookup<T>());
    };

    // Forget cached results too, e.g. after the configuration's credentials change
    const clear = () => {
        cache.current.clear();
        inFlight.current.clear();
        reset();
    };

    return { state, run, reset, clear };
}

const requestKey = (...parts: (string | boolean | undefined)[]) => JSON.stringify(parts);

function usePmsLookupContainer() {
    const [configId, setConfigId] = useState<string | null>(null);
    // The configuration selected right now, for requests that finish after the selection changed
    const currentConfigId = useRef<string | null>(null);
    currentConfigId.current = configId;
    const [officeId, setOfficeId] = useState<string>('');
    // Kept here so the procedure types filter survives tab switches and matches the loaded list
    const [onlyBookable, setOnlyBookable] = useState(false);

    const offices = useLookup<PmsOfficeModel[]>();
    const procedureTypes = useLookup<PmsProcedureTypeModel[]>();
    const providers = useLookup<PmsProviderModel[]>();
    // Open slots and patient searches change over time, so these always go to the PMS when asked
    const slots = useLookup<PmsSlotModel[]>(false);
    const patients = useLookup<PmsPatientModel[]>(false);
    const patientDetail = useLookup<PmsPatientModel>();
    const cancellationCodes = useLookup<PmsCodeModel[]>();
    const referralTypes = useLookup<PmsCodeModel[]>();
    const cloud9Employees = useLookup<PmsProviderModel[]>();
    const cloud9ScheduleViews = useLookup<Cloud9ScheduleViewModel[]>();
    // What the server has cached; it changes whenever anything is pulled, so it's read fresh each time
    const cacheSummary = useLookup<PmsCacheCategoryModel[]>(false);

    const allLookups = [offices, procedureTypes, providers, slots, patients, patientDetail, cancellationCodes, referralTypes, cloud9Employees, cloud9ScheduleViews, cacheSummary];

    // Switching configs clears every result so data from one PMS never shows under another
    const selectConfig = (newConfigId: string | null) => {
        setConfigId(newConfigId);
        setOfficeId('');
        allLookups.forEach(lookup => lookup.reset());
    };

    // Edited configurations keep their ID, so drop everything pulled with the old settings
    const clearConfig = () => {
        setOfficeId('');
        allLookups.forEach(lookup => lookup.clear());
    };

    // Results on this page that came from each server cache category, so clearing one doesn't throw away the rest
    const clearCachedResults = (type?: string) => {
        switch (type) {
            case 'providers':
                providers.clear();
                cloud9Employees.clear();
                break;
            case 'procedure-types':
                procedureTypes.clear();
                break;
            case 'schedule':
                cloud9ScheduleViews.clear();
                slots.clear();
                break;
            case 'available-slots':
                slots.clear();
                break;
            case 'insurance':
            case 'referral-sources':
            case 'caller-lookups':
            case 'other':
                break;
            // Offices or everything: the office list itself may change, so start over
            default:
                clearConfig();
        }
    };

    // Results that were pulled for one office are stale once the office changes
    const selectOffice = (newOfficeId: string) => {
        setOfficeId(newOfficeId);
        [procedureTypes, providers, slots, patients, patientDetail].forEach(lookup => lookup.reset());
    };

    const missingConfig =<T>(): Promise<IResult<T>> => Promise.resolve({
        resultType: "Invalid",
        errors: ['Select a configuration first'],
        data: null
    } as IResult<T>);

    return {
        configId,
        officeId,
        setOfficeId: selectOffice,
        selectConfig,
        clearConfig,
        offices: offices.state,
        procedureTypes: procedureTypes.state,
        providers: providers.state,
        slots: slots.state,
        patients: patients.state,
        patientDetail: patientDetail.state,
        cancellationCodes: cancellationCodes.state,
        referralTypes: referralTypes.state,
        cloud9Employees: cloud9Employees.state,
        cloud9ScheduleViews: cloud9ScheduleViews.state,
        procedureTypesOnlyBookable: onlyBookable,
        // Pass force (the Refresh buttons) to skip results already pulled for the same request
        loadOffices: (force?: boolean) => configId
            ? offices.run(requestKey(configId), () => voicifyApi.getPmsOffices(configId), force)
            : missingConfig<PmsOfficeModel[]>(),
        loadProcedureTypes: (bookable: boolean = onlyBookable, force?: boolean) => {
            if (!configId) return missingConfig<PmsProcedureTypeModel[]>();
            setOnlyBookable(bookable);
            return procedureTypes.run(requestKey(configId, officeId, bookable), () => voicifyApi.getPmsProcedureTypes(configId, officeId, bookable), force);
        },
        loadProviders: (force?: boolean) => configId
            ? providers.run(requestKey(configId, officeId), () => voicifyApi.getPmsProviders(configId, officeId || undefined), force)
            : missingConfig<PmsProviderModel[]>(),
        loadSlots: (request: Omit<PmsSlotsRequest, 'officeId'>) => configId
            ? slots.run(requestKey(configId, officeId, JSON.stringify(request)), () => voicifyApi.getPmsAvailableSlots(configId, { ...request, officeId }))
            : missingConfig<PmsSlotModel[]>(),
        searchPatients: (request: Omit<PmsPatientSearchRequest, 'officeId'>) => configId
            ? patients.run(requestKey(configId, officeId, JSON.stringify(request)), () => voicifyApi.searchPmsPatients(configId, { ...request, officeId: officeId || undefined }))
            : missingConfig<PmsPatientModel[]>(),
        loadPatient: (patientId: string) => configId
            ? patientDetail.run(requestKey(configId, officeId, patientId), () => voicifyApi.getPmsPatient(configId, patientId, officeId || undefined))
            : missingConfig<PmsPatientModel>(),
        clearPatientDetail: patientDetail.reset,
        clearPatients: patients.reset,
        loadCancellationCodes: (force?: boolean) => configId
            ? cancellationCodes.run(requestKey(configId), () => voicifyApi.getDenticonCancellationCodes(configId), force)
            : missingConfig<PmsCodeModel[]>(),
        loadReferralTypes: (force?: boolean) => configId
            ? referralTypes.run(requestKey(configId), () => voicifyApi.getDenticonReferralTypes(configId), force)
            : missingConfig<PmsCodeModel[]>(),
        loadCloud9Employees: (force?: boolean) => configId
            ? cloud9Employees.run(requestKey(configId), () => voicifyApi.getCloud9Employees(configId), force)
            : missingConfig<PmsProviderModel[]>(),
        loadCloud9ScheduleViews: (force?: boolean) => configId
            ? cloud9ScheduleViews.run(requestKey(configId), () => voicifyApi.getCloud9ScheduleViews(configId), force)
            : missingConfig<Cloud9ScheduleViewModel[]>(),
        cacheSummary: cacheSummary.state,
        loadCacheSummary: () => configId
            ? cacheSummary.run(requestKey(configId, 'cache'), () => voicifyApi.getPmsCacheSummary(configId))
            : missingConfig<PmsCacheCategoryModel[]>(),
        // Refresh only skips this page's copy; the server keeps its own PMS cache (offices for up to 48 hours).
        // This deletes one category of the server's copy (or all of it without a type), then drops the results shown
        // here that came from it so they're pulled fresh from the PMS.
        clearServerCache: async (type?: string): Promise<IResult<PmsCacheClearModel>> => {
            if (!configId) return missingConfig<PmsCacheClearModel>();
            const requestedFor = configId;
            const result = await voicifyApi.clearPmsLookupCache(configId, type)
                .catch(e => ({ resultType: "InternalError", errors: [e?.toString() || 'Unknown error'], data: null } as IResult<PmsCacheClearModel>));
            // If another configuration was picked meanwhile, the results shown now are its own, so leave them alone
            if (result.resultType === "Ok" && currentConfigId.current === requestedFor) {
                clearCachedResults(type);
            }
            return result;
        }
    };
}

const PmsLookupContainer = createContainer(usePmsLookupContainer);

export default PmsLookupContainer;
